import React, {useEffect, useRef} from 'react';

const SCRIPT_ID = 'cf-turnstile-api';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let turnstilePromise;

function loadTurnstileScript() {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }
    if (turnstilePromise) {
        return turnstilePromise;
    }

    turnstilePromise = new Promise((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID);
        const script = existing || document.createElement('script');
        const finish = () => resolve(window.turnstile);
        const fail = () => {
            turnstilePromise = null;
            reject(new Error('Turnstile failed to load'));
        };

        script.addEventListener('load', finish);
        script.addEventListener('error', fail);
        if (existing) {
            return;
        }

        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        document.head.appendChild(script);
    });

    return turnstilePromise;
}

const TurnstileWidget = ({siteKey, onToken, resetRef}) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useEffect(() => {
        if (!resetRef) {
            return undefined;
        }
        resetRef.current = () => {
            onTokenRef.current('');
            if (window.turnstile && widgetIdRef.current != null) {
                window.turnstile.reset(widgetIdRef.current);
            }
        };
        return () => {
            resetRef.current = null;
        };
    }, [resetRef]);

    useEffect(() => {
        if (!siteKey) {
            return undefined;
        }

        let cancelled = false;
        onTokenRef.current('');

        loadTurnstileScript().then((turnstile) => {
            if (cancelled || !containerRef.current || !turnstile) {
                return;
            }
            widgetIdRef.current = turnstile.render(containerRef.current, {
                sitekey: siteKey,
                theme: 'dark',
                callback: (token) => onTokenRef.current(token),
                'expired-callback': () => onTokenRef.current(''),
                'error-callback': () => onTokenRef.current(''),
            });
        }).catch(() => {
            onTokenRef.current('');
        });

        return () => {
            cancelled = true;
            if (window.turnstile && widgetIdRef.current != null) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [siteKey]);

    if (!siteKey) {
        return null;
    }

    return <div className="turnstile-widget" ref={containerRef}/>;
};

export default TurnstileWidget;
